/**
 * Module that facilitates accessing Vizia via deck share links.
 *
 * > Determines if a deck share link is in use and extracts the share ID from the URL.
 * > Gets the deck share data from Shared Dashboards Service.
 */
define('js/lib/share',[
    'js/config/clientSideConfig',
    'js/lib/communicationErrors',
    'js/views/PasswordInputView',
    'vendor/wrapple'
], function(
    getClientSideConfig,
    communicationErrors,
    PasswordInputView,
    wrapple
) {
    'use strict';

    var passwordInputPopup = null;

    function base64EncodeUtf8(value) {
        // Basic auth expects bytes; encode the JS string as UTF-8 before base64.
        return btoa(encodeURIComponent(value).replace(/%([0-9A-F]{2})/g, function (_, hex) {
            return String.fromCharCode(parseInt(hex, 16));
        }));
    }

    var config = getClientSideConfig();

    // shares to shared decks are namespaced URLs:
    // /i for email shares.
    // /s for signage link shares.
    // The share ID is the part of the URL immediately after the namespace.
    // Matches /c/<uuid> or /i/<uuid> or /s/<uuid> where <uuid> is a standard UUID (8-4-4-4-12 hex)
    var shareRegex = /\/([cis])\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/;

    var share = {
        // These properties are populated by check() when it determines that the
        // URL is an share. The properties are exposed on the module so that
        // they can be used elsewhere in bootstrap.
        shareId: null,
        token: null,
        targetUrl: null,
        isCast: false,

        // Cookie refresh management params
        cookieRefreshTimeout: null,
        cookieRefreshTimeoutMs: 4 * 60 * 1000, // 4 minutes (less than 5 minute expiry)

        // Determines whether the current URL is an share to a shared deck.
        // If so, shareId and otp are populated on this object.
        setParamsFromUrl: function() {
            var location = wrapple.location();

            var shareMatches = location.pathname.match(shareRegex);

            share.shareId = shareMatches ? shareMatches[2] : undefined;
            share.namespace = shareMatches ? shareMatches[1] : undefined; // 'c', 'i', or 's'

            if (share.namespace === 'c') {
                share.isCast = true;
            }

            // Set default target URL
            if (!share.targetUrl) {
                share.targetUrl = config.sdsRootUrl + 'deck/' + share.shareId;
            }
        },

        isShare: function() {
            return !!share.shareId;
        },

        getDeckConfig: function({ isCast, hasCookie, password }) {
            return new Promise(function (resolve, reject) {
                var request = new XMLHttpRequest();

                if (hasCookie) { // Cookie already exists, no need for password
                    share.token = null;
                    share.targetUrl = config.sdsRootUrl + 'deck/' + share.shareId;
                } else if (isCast) { // Cast link doesn't require a password
                    share.token = null;
                    share.targetUrl = config.sdsRootUrl + 'deck/cast/' + share.shareId;
                } else { // Standard share requiring password
                    share.token = base64EncodeUtf8(`:${password}`);
                    share.targetUrl = config.sdsRootUrl + 'deck/' + share.shareId;
                }

                console.info('Target URL:', share.targetUrl);

                request.timeout = config.requestTimeout * 1000;
                request.open('GET', share.targetUrl);

                if(share.token) {
                    request.setRequestHeader('Authorization', 'Basic ' + share.token);
                }

                request.withCredentials = true;

                request.onerror = function () {
                    console.info('Error requesting share');
                    passwordInputPopup.displayError('Network error. Please check your connection and try again.');
                    reject(communicationErrors.networkError);
                };

                request.ontimeout = function () {
                    console.info('Timeout requesting share');
                    passwordInputPopup.displayError('Request timed out. Please try again.');
                };

                request.onload = function (evt) {
                    console.info('Share on load - actual GET request completed');
                    console.info('Response status:', evt.target.status);

                    if (evt.target.status === 401 || evt.target.status === 403) {
                        // Invalid password - show error and allow retry
                        console.info('Authentication failed - invalid password');
                        passwordInputPopup.displayError('Invalid password');
                        return;
                    }

                    if (evt.target.status < 200 || evt.target.status > 299) {
                        console.info('Unexpected status requesting share: ' + evt.target.status);
                        passwordInputPopup.displayError('Unable to access content. Please try again.');
                        return;
                    }

                    var data;
                    try {
                        data = JSON.parse(evt.target.responseText);
                        data.bwApiRootUrl = config.sdsRootUrl + "deck/" + share.shareId;
                        data.token = btoa("placeholder:unused"); // Token is no longer required, but some bundles expect it still and throw an error if not set.
                        console.info('Share data', data);
                    } catch (err) {
                        console.info('Error parsing share data', err);
                        passwordInputPopup.displayError('Invalid response from server. Please try again.');
                        reject(communicationErrors.invalidResponse);
                        return;
                    }

                    console.info('Share retrieved');
                    console.info('Removing password popup');

                    if (passwordInputPopup !== null) {
                        passwordInputPopup.el.remove();
                        console.info('Password popup removed');
                        passwordInputPopup = null;
                    }

                    // Start cookie refresh after successful share retrieval
                    share.startCookieRefresh();

                    resolve(data);
                };

                request.send();
            });
        },

        // Gets the deck share from Shared Dashboards Service. This requires check() to have
        // been run first, since that sets shareId.
        getShare: function() {
            if (!share.shareId) {
                return Promise.reject(new Error('No share ID'));
            }

            if (share.isCast) { // Cast link doesn't require a password
                return share.getDeckConfig({ isCast: true });
            }

            // Get the share from Shared Dashboards Service.
            return new Promise(function (resolve, reject) {
                // First check if cookie exists
                share.refreshCookie()
                    .then(() => {
                        // Cookie exists and is valid - skip password input
                        console.info('sds_resources cookie found, skipping password input');
                        share.getDeckConfig({ hasCookie: true }).then(resolve).catch(reject);
                    })
                    .catch(() => {
                        // No cookie or invalid - show password input
                        console.info('No valid cookie, showing password input');

                        const onSubmit = password => {
                            share.getDeckConfig({ isCast: false, password }).then(resolve).catch(reject);
                        };

                        passwordInputPopup = new PasswordInputView(onSubmit);
                        document.body.appendChild(passwordInputPopup.el);
                        passwordInputPopup.animate();
                    });
            });
        },

        // New method to refresh the session cookie
        refreshCookie: function() {
            if (!share.shareId) {
                console.info('No share ID available for cookie refresh');
                return Promise.reject(new Error('No share ID'));
            }

            console.info('Refreshing SDS session cookie');

            return new Promise(function(resolve, reject) {
                var request = new XMLHttpRequest();

                // Use a lightweight endpoint to refresh the cookie
                // This could be the same deck endpoint or a dedicated keepalive endpoint
                var refreshUrl = share.targetUrl;

                request.timeout = config.requestTimeout * 1000;
                request.open('GET', refreshUrl);

                if (share.token) {
                    request.setRequestHeader('Authorization', 'Basic ' + share.token);
                }

                request.withCredentials = true;

                request.onload = function(evt) {
                    if (evt.target.status >= 200 && evt.target.status < 300) {
                        share.lastRefreshTime = Date.now();
                        console.info('Cookie refreshed successfully');
                        resolve();
                    } else {
                        console.warn('Cookie refresh failed with status:', evt.target.status);
                        reject(new Error('Cookie refresh failed'));
                    }
                };

                request.onerror = function() {
                    console.warn('Cookie refresh network error');
                    reject(new Error('Cookie refresh network error'));
                };

                request.ontimeout = function() {
                    console.warn('Cookie refresh timeout');
                    reject(new Error('Cookie refresh timeout'));
                };

                request.send();

            });
        },

        // Start the periodic cookie refresh
        startCookieRefresh: function() {
            if (share.cookieRefreshTimeout) {
                clearTimeout(share.cookieRefreshTimeout);
            }

            share.cookieRefreshTimeout = setTimeout(function() {
                share.refreshCookie().catch(function(error) {
                    console.error('Scheduled cookie refresh failed:', error);
                    // Could implement retry logic here if needed
                });

                // Schedule the next refresh
                share.startCookieRefresh();
            }, share.cookieRefreshTimeoutMs);

            console.info('Started cookie refresh timer (every', share.cookieRefreshTimeoutMs / 1000, 'seconds)');
        },

        // Stop the periodic cookie refresh
        stopCookieRefresh: function() {
            if (share.cookieRefreshTimeout) {
                clearTimeout(share.cookieRefreshTimeout);
                share.cookieRefreshTimeout = null;
                console.info('Stopped cookie refresh timer');
            }
        }
    };

    return share;
});
